import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaSlidersH } from "react-icons/fa";
import { baseurl } from "../Baseurl";
import axios from "axios";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function CalanderSetting() {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);

  const orgId = localStorage.getItem("organization_id");
  const adminId = localStorage.getItem("adminID");

  // States
  const [defaultWeekStart, setDefaultWeekStart] = useState("Monday");
  const [calendarSyncToggled, setCalendarSyncToggled] = useState(true);
  const [showWorkingHours, setShowWorkingHours] = useState(false);
  const [showTimeSettings, setShowTimeSettings] = useState(false);
  const [timeZone, setTimeZone] = useState("IST");

  // Working Hours defaults (9 AM – 6 PM)
  const [startTime, setStartTime] = useState(() => {
    const d = new Date();
    d.setHours(9, 0, 0, 0);
    return d;
  });
  const [endTime, setEndTime] = useState(() => {
    const d = new Date();
    d.setHours(18, 0, 0, 0);
    return d;
  });

  // Toast
  const showToast = (message, type = "success") => {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: type,
      title: message,
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
    });
  };

  // Save settings
  const sendSetting = async (settingType, data) => {
    const payload = { adminId, orgId, type: settingType, ...data };
    try {
      await axios.post(`${baseurl}updateCalendarSetting`, payload);
    } catch (error) {
      console.error("❌ Error saving setting:", error);
    }
  };

  // Load existing settings
  const loadSettings = async () => {
    try {
      const { data } = await axios.get(
        `${baseurl}getCalendarSetting?adminId=${adminId}&orgId=${orgId}`
      );

      if (data && data.data) {
        const s = data.data;
        setDefaultWeekStart(s.default_week_start || "Monday");
        setTimeZone(s.time_zone || "IST");
        setCalendarSyncToggled(s.calendar_sync_enabled ?? true);

        if (s.start_time && s.end_time) {
          const start = new Date();
          const end = new Date();

          const [sh, sm] = s.start_time.split(":");
          const [eh, em] = s.end_time.split(":");

          start.setHours(sh, sm, 0, 0);
          end.setHours(eh, em, 0, 0);

          setStartTime(start);
          setEndTime(end);
        }
      }
    } catch (err) {
      console.error("❌ Failed to load calendar settings:", err);
    }
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem("darkMode") === "true";
    setDarkMode(savedTheme);
    document.body.classList.toggle("dark", savedTheme);

    if (!adminId) navigate("/");
    else loadSettings();
  }, [navigate, adminId]);

  return (
    <>
      <div className="settings-header">
        <h3>Calendar & Time</h3>
      </div>

      {/* Default Week Start */}
      <div className="settings-section">
        <h4 className="section-title">Default Week Start</h4>
        <p className="section-description">
          Choose the first day of the week (Sunday or Monday) for calendars and
          reports.
        </p>


        <div className="default-week-start-options">
          {["Sunday", "Monday"].map((day) => (
            <button
              key={day}
              className={`week-start-button ${
                defaultWeekStart === day ? "active" : ""
              }`}
              onClick={() => {
                setDefaultWeekStart(day);
                sendSetting("week_start", { defaultWeekStart: day });
              }}
            >
              {day}
            </button>
          ))}
        </div>

        
      </div>

      {/* Working Hours */}
      <div className="settings-section">
        <div
          className="settings-option-item"
          onClick={() => setShowWorkingHours(!showWorkingHours)}
          style={{ cursor: "pointer" }}
        >
          <h4 className="section-title-inline">Working Hours</h4>
          <FaSlidersH className="toggle-settings-icon" />
        </div>

        <p className="section-description">
          Define standard working hours to avoid unnecessary alerts or reminders
          during off-hours.
        </p>


        {showWorkingHours && (
          <div className="extra-options" style={{ display: "flex", gap: "20px" }}>
            <div>
              <label>Start:</label>
              <DatePicker
                selected={startTime}
                onChange={(time) => {
                  setStartTime(time);
                  sendSetting("working_hours", {
                    startTime: time.toTimeString().slice(0, 5),
                    endTime: endTime.toTimeString().slice(0, 5),
                  });
                }}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                dateFormat="h:mm aa"
                className="time-picker form-control"
              />
            </div>
            <div>
              <label>End:</label>
              <DatePicker
                selected={endTime}
                onChange={(time) => {
                  setEndTime(time);
                  sendSetting("working_hours", {
                    startTime: startTime.toTimeString().slice(0, 5),
                    endTime: time.toTimeString().slice(0, 5),
                  });
                }}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                dateFormat="h:mm aa"
                className="time-picker form-control"
              />
            </div>
          </div>
        )}
      </div>

      {/* Time Settings */}
      <div className="settings-section">
        <div
          className="settings-option-item"
          onClick={() => setShowTimeSettings(!showTimeSettings)}
          style={{ cursor: "pointer" }}
        >
          <h4 className="section-title-inline">Time Settings</h4>
          <FaSlidersH className="toggle-settings-icon" />
        </div>


        <p className="section-description">
          Set local time-zone preferences for remote or distributed teams so
          task deadline and calendar events align correctly.
        </p>
        {showTimeSettings && (
          <div className="extra-options">
            <label>
              Default Time Zone:
              <select
                style={{ marginLeft: "10px" }}
                className="form-select"
                value={timeZone}
                onChange={(e) => {
                  setTimeZone(e.target.value);
                  sendSetting("time_zone", { timeZone: e.target.value });
                }}
              >
                <option value="IST">IST (India)</option>
                <option value="UTC">UTC</option>
                <option value="EST">EST</option>
                <option value="PST">PST</option>
              </select>
            </label>
          </div>
        )}
      </div>

      {/* Calendar Sync */}
      <div className="settings-section">
        <div className="toggle-item">
          <h4 className="section-title-inline">Calendar Sync</h4>
          <label className="switch">
            <input
              type="checkbox"
              checked={calendarSyncToggled}
              onChange={() => {
                const newValue = !calendarSyncToggled;
                setCalendarSyncToggled(newValue);
                sendSetting("calendar_sync", {
                  calendarSyncToggled: newValue,
                });
                showToast(
                  newValue
                    ? "✅ Calendar Sync enabled"
                    : "❌ Calendar Sync disabled"
                );
              }}
            />
            <span className="slider round"></span>
          </label>
        </div>

      <p className="section-description">
          Sync your existing calendar events.
        </p>



      </div>
    </>
  );
}
